import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, Share2, Star, Play, Maximize2, Eye, BookOpen, Clock, Calendar,
  CheckCircle, ChevronRight, ArrowRight, Loader2, Lock, X, Pause, Link2
} from 'lucide-react'
import { api } from '@/lib/api'
import VideoPlayer from '@/components/VideoPlayer'
import { useAuth } from '@/context/AuthContext'
import { formatPrice, formatDate, getTeacherName, formatName, resolveImg } from '@/lib/helpers'

const API_BASE = import.meta.env.VITE_API_URL || ''

type TabKey = 'intro' | 'lessons' | 'reviews'

const tabs: { key: TabKey; label: string }[] = [
  { key: 'intro', label: 'Танилцуулга' },
  { key: 'lessons', label: 'Хичээлүүд' },
  { key: 'reviews', label: 'Сэтгэгдэл' },
]
const formatDuration = (seconds: number) => {
  if(!seconds || isNaN(seconds)) return '-'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
export default function CourseDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<TabKey>('intro')
  const [course, setCourse] = useState<any>(null)
  const [lessons, setLessons] = useState<any[]>([])
  const [reviews, setReviews] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  // Enrollment
  const [enrolled, setEnrolled] = useState(false)
  const [enrollment, setEnrollment] = useState<any>(null)
  const [completedLessonIds, setCompletedLessonIds] = useState<string[]>([])

  // Premium
  const [isPremiumUser, setIsPremiumUser] = useState(false)

  const canAccess = enrolled || (isPremiumUser && course?.priceType === 'premium')

  // Video player
  const [activeLesson, setActiveLesson] = useState<any>(null)

  // Share
  const [shareToast, setShareToast] = useState(false)

  // Review form
  const [reviewRating, setReviewRating] = useState(0)
  const [reviewHover, setReviewHover] = useState(0)
  const [reviewText, setReviewText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [reviewSuccess, setReviewSuccess] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!id) return
    setLoading(true)
    Promise.all([
      api.courses.getById(id).then(setCourse),
      api.courses.getLessons(id).then(setLessons).catch(() => {}),
      api.courses.getReviews(id).then(res => setReviews(res.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false))
  }, [id])
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
      document.removeEventListener('click', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])
  // Check premium status
  useEffect(() => {
    if (!user) return
    api.premium.getStatus().then((res: any) => setIsPremiumUser(!!res.isPremium)).catch(() => {})
  }, [user])

  // Check enrollment
  useEffect(() => {
    if (!id || !user) return
    api.courses.getEnrollment(id)
      .then((res: any) => {
        const hasRealEnrollment = !!res.enrollment
        setEnrolled(hasRealEnrollment)
        setEnrollment(res.enrollment)
        if (res.enrollment?.completedLessons) {
          setCompletedLessonIds(res.enrollment.completedLessons.map((l: any) => l.toString()))
        }
        if (hasRealEnrollment) {
          api.courses.getLessons(id).then(setLessons).catch(() => {})
        }
      })
      .catch(() => {})
  }, [id, user])

  const handleSubmitReview = async () => {
    if (!id || !reviewRating || !reviewText.trim()) return
    setSubmitting(true)
    try {
      await api.courses.addReview(id, { rating: reviewRating, comment: reviewText })
      const res = await api.courses.getReviews(id)
      setReviews(res.data || [])
      setReviewRating(0)
      setReviewText('')
      setReviewSuccess(true)
      setTimeout(() => setReviewSuccess(false), 3000)
    } catch { /* */ }
    setSubmitting(false)
  }

  const handleNativeShare = async () => {
    setDropdownOpen(false)
    const url = `${window.location.origin}/courses/${id}`
    try {
      await navigator.share({ title: course.name, url })
      setTimeout(() => setShareToast(false), 3000)
    } catch {}
  }
  const handleCopyLink = async () => {
    setDropdownOpen(false)
    const url = `${window.location.origin}/courses/${id}`
    try {
      await navigator.clipboard.writeText(url)
      setShareToast(true)
      setTimeout(() => setShareToast(false), 2000)
      } catch {
        window.prompt('Холбоосыг хуулна уу:', url)
    }
  }
  // lessons дахин татагдахад activeLesson-г шинэчлэх
  useEffect(() => {
    if (!activeLesson) return
    const updated = lessons.find((l) => l._id === activeLesson._id)
    if (updated) setActiveLesson(updated)
  }, [lessons])

  const handlePlayLesson = (lesson: any) => {
    const canPlay = canAccess || lesson.isFree
    if (!canPlay) return
    setActiveLesson(lesson)
    setActiveTab('lessons')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Үнэгүй эхлэх хичээлийг олох

  const getPlayableLesson = () => {
    if (!lessons.length) return null
    if (canAccess) {
      const next = lessons.find(l => !completedLessonIds.includes(l._id))
      return next || lessons[0]
    }
    return lessons.find(l => l.isFree) || null
  }
  const previewLesson = getPlayableLesson()

  const handleLessonComplete = async (lessonId: string) => {
    if (!id || !canAccess) return
    try {
      const res = await api.courses.updateProgress(id, lessonId)
      setEnrollment(res)
      setCompletedLessonIds(res.completedLessons?.map((l: any) => l.toString()) || [])
    } catch {}
  }

  if (loading || !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary-500" />
      </div>
    )
  }

  const teacherName = getTeacherName(course)
  const teacher = course.teacherId
  const teacherAvatar = teacher?.userId?.avatar || teacher?.avatar
  const teacherSpecialty = teacher?.specialty || teacher?.title || ''
  const teacherBio = teacher?.bio || ''
  const teacherId = teacher?._id

  const price = course.price || 0
  const lessonCount = lessons.length || course.lessonCount || 0
  const studentCount = course.studentCount || course.enrolledCount || 0
  const duration = lessons.reduce((sum, l) => sum + (l.duration || 0), 0) || course.duration || course.totalDuration || 0

  // Progress from enrollment
  const progress = enrollment?.progress || 0
  
  // Rating stats
  const avgRating = course.rating || 0
  const ratingCounts = [0, 0, 0, 0, 0]
  reviews.forEach(r => {
    if (r.rating >= 1 && r.rating <= 5) ratingCounts[r.rating - 1]++
  })
  const maxCount = Math.max(...ratingCounts, 1)

  // Check if user already reviewed
  const hasReviewed = reviews.some((r: any) => r.userId?._id === user?._id)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200">
      <div className="max-w-[1024px] mx-auto px-4 flex items-center justify-between py-4">
        <button
          onClick={() => navigate(-1)}
          className="bg-white border border-gray-200 rounded-full px-4 py-2.5 flex items-center gap-2.5 text-sm font-medium text-gray-800 shadow-md hover:bg-gray-50 transition cursor-pointer"
        >
          <ArrowLeft className="size-5" />
          Буцах
        </button>
        <div className="flex items-center gap-2">
          {enrolled && (
            <span className="bg-green-50 text-green-600 text-xs font-semibold px-3 py-1.5 rounded-full border border-green-200">
              Бүртгэлтэй
            </span>
          )}
          <div ref={dropdownRef} className="relative">
          <button
            onClick={() => setDropdownOpen((v) => !v)}
            className="bg-white border border-gray-200 rounded-full p-2.5 shadow-md hover:bg-gray-50 transition cursor-pointer"
          >
            <Share2 className="size-5 text-gray-600" />
            </button>
          {dropdownOpen && (
              <div className="absolute right-0 top-12 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50">
                {typeof navigator.share === 'function' && (
                  <button
                    onClick={handleNativeShare}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <Share2 className="size-4 text-gray-400" />
                    Хуваалцах
                  </button>
                )}
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  {shareToast ? (
                    <>
                      <span className="size-4 text-green-500">✓</span>
                      Хуулагдлаа
                    </>
                  ) : (
                    <>
                      <Link2 className="size-4 text-gray-400" />
                      Линк хуулах
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Share toast */}
      {shareToast && (
        <div
          className="fixed top-20 left-1/2 -translate-x-1/2 z-50"
          style={{ animation: 'shareToastIn 0.25s ease-out' }}
        >
          <div className="bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-full shadow-lg flex items-center gap-2">
            <CheckCircle className="size-4 text-green-400" />
            Холбоос хуулагдлаа
          </div>
        </div>
      )}
      <style>{`
        @keyframes shareToastIn {
          from { opacity: 0; transform: translate(-50%, -8px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>

      <div className="max-w-[1024px] mx-auto px-4 flex flex-col lg:flex-row gap-8">
        {/* Left column - Main content */}
        <div className="flex-1 pt-8 lg:pt-[60px] pb-20 flex flex-col gap-9">
          {/* Category + Rating */}
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <span className="bg-primary-100 text-primary-500 text-xs font-semibold px-2 py-1 rounded-full uppercase">
                {course.category || 'БУДДЫН ХИЧЭЭЛ'}
              </span>
              <span className="flex items-center gap-1">
                <Star className="size-5 fill-yellow-400 text-yellow-400" />
                <span className="text-base font-semibold text-yellow-500">{avgRating.toFixed(1)}</span>
              </span>
            </div>

            {/* Title */}
            <h1 className="text-2xl md:text-4xl font-bold text-black">{course.title}</h1>
          </div>

          {/* Video Player / Thumbnail */}
          {activeLesson ? (
            <div className="relative rounded-3xl overflow-hidden border border-gray-200 shadow-lg bg-black">
              <div className="flex items-center justify-between bg-gray-900 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Play className="size-4 text-primary-400 shrink-0" />
                  <span className="text-sm font-medium text-white truncate">{activeLesson.title}</span>
                  {activeLesson.isFree && !enrolled && (
                    <span className="text-[10px] font-semibold text-green-300 bg-green-900/60 px-2 py-0.5 rounded-full shrink-0">
                      Үнэгүй
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setActiveLesson(null)}
                  className="text-gray-400 hover:text-white transition p-1"
                >
                  <X className="size-5" />
                </button>
              </div>
              {activeLesson.videoUrl ? (
                <VideoPlayer
                  src={resolveImg(activeLesson.videoUrl) || ''}
                  title={activeLesson.title}
                  autoPlay
                  onEnded={() => handleLessonComplete(activeLesson._id)}
                />
              ) : activeLesson.isLocked && !enrolled ? (
                <div className="w-full aspect-video bg-gray-900 flex flex-col items-center justify-center text-center px-6">
                  <Lock className="size-10 text-gray-500 mb-3" />
                  {course?.priceType === 'premium' ? (
                    <>
                      <p className="text-sm font-medium text-gray-300">Энэ хичээлийг үзэхийн тулд Premium эрх шаардлагатай</p>
                      <button
                        onClick={() => { if (!user) { navigate('/login'); return } navigate('/premium') }}
                        className="mt-4 bg-[#ff4d00] text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-orange-600 transition cursor-pointer"
                      >
                        Premium эрх авах
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-300">Энэ хичээлийг үзэхийн тулд сургалтыг худалдан аваарай</p>
                      <button
                        onClick={() => navigate(`/payment/${id}`)}
                        className="mt-4 bg-primary-500 text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-primary-600 transition cursor-pointer"
                      >
                        Худалдан авах
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <div className="w-full aspect-video bg-gray-900 flex flex-col items-center justify-center text-center px-6">
                  <Lock className="size-10 text-gray-500 mb-3" />
                  <p className="text-sm font-medium text-gray-300">
                    Энэ хичээлд видео байршуулагдаагүй байна
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Админ хэсгээс видеог нэмнэ үү
                  </p>
                </div>
              )}
              {/* Mark complete button */}
              {enrolled && !completedLessonIds.includes(activeLesson._id) && (
                <div className="bg-gray-900 px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-gray-400">Хичээл дууссан уу?</span>
                  <button
                    onClick={() => handleLessonComplete(activeLesson._id)}
                    className="flex items-center gap-1.5 text-sm font-medium text-primary-400 hover:text-primary-300 transition"
                  >
                    <CheckCircle className="size-4" />
                    Дуусгасан гэж тэмдэглэх
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="relative rounded-3xl overflow-hidden border border-gray-200 shadow-lg aspect-[1024/620]">
              {course.thumbnail || course.thumbnailUrl || course.imageUrl || course.videoThumbnail ? (
                <img
                  src={resolveImg(course.thumbnail || course.thumbnailUrl || course.imageUrl || course.videoThumbnail)}
                  alt={course.title}
                  className="absolute inset-0 size-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary-200 to-primary-400" />
              )}

              {/* Play button — play preview / first uncompleted lesson */}
              <button
                onClick={() => {
                  if (previewLesson) handlePlayLesson(previewLesson)
                }}
                disabled={!previewLesson}
                title={previewLesson ? 'Тоглуулах' : 'Үнэгүй танилцуулга байхгүй'}
                className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full p-3 shadow-xs transition ${
                  previewLesson
                    ? 'bg-primary-500 border border-primary-500 hover:bg-primary-600 cursor-pointer'
                    : 'bg-gray-400 border border-gray-400 cursor-not-allowed opacity-60'
                }`}
              >
                <Play className="size-5 text-white fill-white" />
              </button>

              <button className="absolute top-4 right-4 backdrop-blur-sm bg-white/90 rounded-2xl p-3 cursor-pointer hover:bg-white transition">
                <Maximize2 className="size-5 text-gray-600" />
              </button>

              {/* Preview / progress card overlay (clickable) */}
              <button
                onClick={() => previewLesson && handlePlayLesson(previewLesson)}
                disabled={!previewLesson}
                className={`absolute bottom-0 left-0 right-0 p-4 text-left ${
                  previewLesson ? 'cursor-pointer' : 'cursor-default'
                }`}
              >
                <div className="backdrop-blur-sm bg-white rounded-2xl p-4 flex flex-col gap-3 hover:bg-white/95 transition">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {previewLesson?.title || lessons[0]?.title || 'Удиртгал ба суурь ойлголт'}
                    </p>
                    {previewLesson && !enrolled && previewLesson.isFree && (
                      <span className="text-[10px] font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full shrink-0">
                        Үнэгүй
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-primary-600 rounded-full" style={{ width: `${progress || 3}%` }} />
                    </div>
                    <span className="text-sm font-medium text-gray-700 shrink-0">
                      {progress}%
                    </span>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex flex-col gap-6">
            <div className="bg-gray-100 rounded-full p-1 flex gap-1 self-start">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-white text-black shadow-md'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <hr className="border-gray-200" />

            {/* Tab Content */}
            {activeTab === 'intro' && (
              <div className="flex flex-col gap-6">
                <p className="text-base text-gray-600 italic leading-relaxed">
                  {course.description || 'Энэхүү сургалт нь Буддын гүн ухааны суурь ойлголтуудыг олгох бөгөөд бясалгал, дотоод сэтгэлийн амар амгаланг олох анхан шатны мэдлэгийг багтаасан болно.'}
                </p>
                <div className="flex items-center">
                  <div className="flex-1 flex flex-col gap-1">
                    <span className="text-sm text-gray-600">Сурагч</span>
                    <span className="flex items-center gap-1 text-base font-medium text-black">
                      <Eye className="size-4 text-gray-500" />
                      {studentCount}
                    </span>
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    <span className="text-sm text-gray-600">Хичээл</span>
                    <span className="flex items-center gap-1 text-base font-medium text-black">
                      <BookOpen className="size-4 text-gray-500" />
                      {lessonCount} хичээл
                    </span>
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    <span className="text-sm text-gray-600">Нийт хугацаа</span>
                    <span className="flex items-center gap-1 text-base font-medium text-black">
                      <Clock className="size-4 text-gray-500" />
                      {formatDuration(duration || 0)} сек
                    </span>
                  </div>
                  <div className="flex-1 flex flex-col gap-1">
                    <span className="text-sm text-gray-600">Сүүлд шинэчлэгдсэн</span>
                    <span className="flex items-center gap-1 text-base font-medium text-black">
                      <Calendar className="size-4 text-gray-500" />
                      {course.updatedAt ? formatDate(course.updatedAt) : ''}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'lessons' && (
              <div className="flex flex-col gap-4">
                {/* Progress */}
                {enrolled && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Явц</span>
                    <span className="text-sm font-medium text-gray-700">{progress}%</span>
                  </div>
                )}

                {/* Lesson list */}
                <div className="flex flex-col gap-2">
                  {lessons.map((lesson, index) => {
                    const isCompleted = completedLessonIds.includes(lesson._id)
                    const isActive = activeLesson?._id === lesson._id
                    const canPlay = canAccess || lesson.isFree
                    return (
                      <button
                        key={lesson._id || index}
                        onClick={() => canPlay && handlePlayLesson(lesson)}
                        disabled={!canPlay}
                        className={`flex items-center gap-4 px-4 py-3 rounded-xl transition text-left ${
                          isActive
                            ? 'bg-primary-50 border border-primary-300 shadow-sm'
                            : canPlay
                              ? 'bg-white border border-gray-200 hover:border-primary-300 hover:bg-primary-50/30 cursor-pointer'
                              : 'bg-gray-50 border border-gray-200 opacity-70 cursor-not-allowed'
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle className="size-5 text-green-500 shrink-0" />
                        ) : canPlay ? (
                          <span className="size-5 flex items-center justify-center text-sm font-medium text-gray-400 shrink-0">
                            {index + 1}
                          </span>
                        ) : (
                          <Lock className="size-4 text-gray-400 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isActive ? 'text-primary-700' : 'text-gray-800'}`}>
                            {lesson.title}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-400">
                              {formatDuration(lesson.duration || 12)} сек
                            </span>
                            {lesson.isFree && !enrolled && (
                              <span className="text-[10px] font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                Үнэгүй
                              </span>
                            )}
                            {lesson.isMemberOnly && (
                              <span className="text-[10px] font-semibold text-green-700 bg-green-100 px-1.5 py-0.5 rounded">
                                Зөвхөн гишүүдэд
                              </span>
                            )}
                          </div>
                        </div>
                        {canPlay && (
                          <div className="shrink-0">
                            {isActive ? (
                              <Pause className="size-5 text-primary-500" />
                            ) : (
                              <Play className="size-5 text-gray-400" />
                            )}
                          </div>
                        )}
                      </button>
                    )
                  })}

                  {lessons.length === 0 && (
                    <div className="text-center py-12 text-gray-400">
                      <BookOpen className="size-8 mx-auto mb-3 opacity-50" />
                      <p>Хичээл одоохондоо байхгүй байна</p>
                    </div>
                  )}

                  {!enrolled && lessons.length > 0 && (
                    <div className="bg-primary-50 border border-primary-200 rounded-xl px-4 py-3 mt-2">
                      <p className="text-sm text-primary-700">
                        Бүх хичээлийг үзэхийн тулд сургалтыг худалдан аваарай.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="flex flex-col gap-8">
                {/* Rating Summary */}
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <div className="flex gap-8">
                    <div className="text-center shrink-0">
                      <p className="text-5xl font-bold text-gray-900">{avgRating.toFixed(1)}</p>
                      <div className="flex items-center gap-0.5 mt-2 justify-center">
                        {[1, 2, 3, 4, 5].map(i => (
                          <Star key={i} className={`size-4 ${i <= Math.round(avgRating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Нийт {reviews.length} үнэлгээ</p>
                    </div>
                    <div className="flex-1 flex flex-col gap-2 justify-center">
                      {[5, 4, 3, 2, 1].map(star => {
                        const count = ratingCounts[star - 1]
                        const pct = reviews.length > 0 ? Math.round((count / reviews.length) * 100) : 0
                        return (
                          <div key={star} className="flex items-center gap-3">
                            <span className="text-sm text-gray-500 w-3">{star}</span>
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-primary-500 rounded-full transition-all"
                                style={{ width: `${(count / maxCount) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-400 w-8 text-right">{pct}%</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* Review Form — only for enrolled users who haven't reviewed yet */}
                {enrolled && !hasReviewed && (
                  <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <p className="text-sm font-semibold text-gray-800 mb-3">Үнэлгээ болон сэтгэгдэл үлдээх</p>

                    {reviewSuccess && (
                      <div className="bg-green-50 border border-green-200 text-green-700 text-sm font-medium rounded-xl px-4 py-3 mb-4 flex items-center gap-2">
                        <CheckCircle className="size-4 shrink-0" />
                        Үнэлгээ амжилттай илгээгдлээ!
                      </div>
                    )}

                    <div className="flex items-center gap-1 mb-4">
                      {[1, 2, 3, 4, 5].map(i => (
                        <button
                          key={i}
                          onMouseEnter={() => setReviewHover(i)}
                          onMouseLeave={() => setReviewHover(0)}
                          onClick={() => setReviewRating(i)}
                          className="cursor-pointer"
                        >
                          <Star className={`size-6 ${
                            i <= (reviewHover || reviewRating)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`} />
                        </button>
                      ))}
                      {reviewRating > 0 && (
                        <span className="text-sm text-gray-500 ml-2">
                          {['', 'Муу', 'Дунд', 'Сайн', 'Маш сайн', 'Гайхалтай'][reviewRating]}
                        </span>
                      )}
                    </div>
                    <textarea
                      value={reviewText}
                      onChange={(e) => setReviewText(e.target.value)}
                      placeholder="Таны сэтгэгдэл..."
                      className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none h-[100px] focus:outline-none focus:border-primary-500"
                    />
                    <button
                      onClick={handleSubmitReview}
                      disabled={submitting || !reviewRating || !reviewText.trim()}
                      className="mt-3 bg-primary-500 text-white text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-primary-600 transition disabled:opacity-50 cursor-pointer"
                    >
                      {submitting ? 'Илгээж байна...' : 'Илгээх'}
                    </button>
                  </div>
                )}

                {/* Not enrolled notice */}
                {!enrolled && user && (
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 text-center">
                    <p className="text-sm text-gray-500">Үнэлгээ өгөхийн тулд сургалтыг худалдан аваарай.</p>
                  </div>
                )}

                {/* Already reviewed notice */}
                {enrolled && hasReviewed && (
                  <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center">
                    <p className="text-sm text-green-700 font-medium">Та энэ сургалтанд аль хэдийн үнэлгээ өгсөн байна.</p>
                  </div>
                )}

                {/* Review List */}
                <div className="flex flex-col gap-6">
                  {reviews.map((review: any) => (
                    <div key={review._id} className="flex gap-3">
                      <div className="size-9 rounded-full bg-gray-200 shrink-0 overflow-hidden">
                        {review.userId?.avatar && (
                          <img src={review.userId.avatar} alt="" className="size-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-800">
                            {formatName(review.userId?.fullName) || 'Хэрэглэгч'}
                          </span>
                          <span className="text-xs text-gray-400">
                            {review.createdAt ? formatDate(review.createdAt) : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-0.5 mt-1">
                          {[1, 2, 3, 4, 5].map(i => (
                            <Star key={i} className={`size-3 ${
                              i <= (review.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                            }`} />
                          ))}
                        </div>
                        <p className="text-sm text-gray-600 mt-2 leading-relaxed">{review.comment}</p>
                      </div>
                    </div>
                  ))}

                  {reviews.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      <Star className="size-8 mx-auto mb-3 opacity-50" />
                      <p>Одоохондоо үнэлгээ алга</p>
                    </div>
                  )}
                </div>

                {reviews.length > 4 && (
                  <button className="w-full border border-gray-200 rounded-xl py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition cursor-pointer">
                    Илүү сэтгэгдэл харах
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right sidebar */}
        <div className="w-full lg:w-[374px] shrink-0 pt-0 lg:pt-[60px]">
          <div className="bg-white border border-gray-200 rounded-xl shadow-xs p-6 flex flex-col gap-8 lg:sticky lg:top-[85px]">
            {/* Price / Status */}
            {enrolled ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="size-5 text-green-500" />
                  <p className="text-base font-bold text-green-600">Бүртгэлтэй</p>
                </div>
                {/* Progress */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-gray-500">Явц</span>
                    <span className="font-semibold text-primary-500">{progress}%</span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-primary-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>
                {/* Continue button */}
                <button
                  onClick={() => {
                    setActiveTab('lessons')
                    // Find next uncompleted lesson
                    const nextLesson = lessons.find(l => !completedLessonIds.includes(l._id))
                    if (nextLesson) handlePlayLesson(nextLesson)
                    else if (lessons.length > 0) handlePlayLesson(lessons[0])
                  }}
                  className="w-full bg-primary-500 border border-primary-500 text-white text-base font-semibold rounded-full py-3 shadow-xs hover:bg-primary-600 transition cursor-pointer flex items-center justify-center gap-2"
                >
                  <Play className="size-5 fill-white" />
                  Үргэлжлүүлэх
                </button>
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-bold text-gray-600 uppercase">Сургалтын үнэ</p>
                  <p className="text-3xl font-semibold text-gray-800">{formatPrice(price)}</p>
                </div>
                {course?.priceType === 'premium' ? (
                  isPremiumUser ? (
                    <button
                      onClick={async () => {
                        if (!id) return
                        if (!user) { navigate('/login'); return }
                        try {
                          await api.courses.enroll(id)
                          setEnrolled(true)
                          const res = await api.courses.getEnrollment(id)
                          setEnrollment(res.enrollment)
                        } catch {}
                      }}
                      className="w-full bg-primary-500 border border-primary-500 text-white text-base font-semibold rounded-full py-3 shadow-xs hover:bg-primary-600 transition cursor-pointer"
                    >
                      Бүртгүүлэх
                    </button>
                  ) : (
                    <button
                      onClick={() => { if (!user) { navigate('/login'); return } navigate('/premium') }}
                      className="w-full bg-[#ff4d00] border border-[#ff4d00] text-white text-base font-semibold rounded-full py-3 shadow-xs hover:bg-orange-600 transition cursor-pointer"
                    >
                      Premium эрх авах
                    </button>
                  )
                ) : price === 0 ? (
                  <button
                    onClick={async () => {
                      if (!id) return
                      if (!user) { navigate('/login'); return }
                      try {
                        await api.courses.enroll(id)
                        setEnrolled(true)
                        const res = await api.courses.getEnrollment(id)
                        setEnrollment(res.enrollment)
                      } catch {}
                    }}
                    className="w-full bg-primary-500 border border-primary-500 text-white text-base font-semibold rounded-full py-3 shadow-xs hover:bg-primary-600 transition cursor-pointer"
                  >
                    Үнэгүй бүртгүүлэх
                  </button>
                ) : (
                  <button
                    onClick={() => navigate(`/payment/${id}`)}
                    className="w-full bg-primary-500 border border-primary-500 text-white text-base font-semibold rounded-full py-3 shadow-xs hover:bg-primary-600 transition cursor-pointer"
                  >
                    Худалдан авах
                  </button>
                )}
              </>
            )}

            {/* What you'll learn */}
            <div className="flex flex-col gap-4">
              <p className="text-sm font-bold text-gray-600 uppercase">Сургалтаас сурах зүйлс:</p>
              <div className="flex flex-col gap-3">
                {(course.learnings || [
                  'Бясалгалын үндсэн техникүүдийг эзэмших',
                  'Сэтгэл санаагаа удирдах, төвлөрөх чадвар',
                  'Өдөр тутмын стрессээс ангижрах арга замууд',
                  'Дотоод амар амгалан, аз жаргалын нууц',
                ]).map((item: string, i: number) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="size-4 text-primary-500 shrink-0 mt-0.5" />
                    <p className="text-sm font-medium text-gray-600">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Teacher info */}
            <p className="text-sm font-bold text-gray-600 uppercase">Багшийн мэдээлэл</p>

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <div className="size-12 rounded-full border-3 border-white overflow-hidden bg-gray-200 shrink-0">
                  {teacherAvatar && (
                    <img src={resolveImg(teacherAvatar)} alt={teacherName} className="size-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-gray-800">{teacherName || 'Багш'}</p>
                  <p className="text-xs font-bold text-[#ff4d00] uppercase">{teacherSpecialty}</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed">
                {teacherBio}
              </p>

              <hr className="border-gray-200" />

              <div className="flex gap-4">
                <div className="flex-1 bg-gray-50 rounded-xl py-3 text-center">
                  <p className="text-sm font-bold text-black">{teacher?.followerCount || 0}</p>
                  <p className="text-sm text-gray-400">Дагагч</p>
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl py-3 text-center">
                  <p className="text-sm font-bold text-black">{teacher?.courseCount || 0}</p>
                  <p className="text-sm text-gray-400">Хичээл</p>
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl py-3 text-center">
                  <p className="text-sm font-bold text-black">{teacher?.postCount || 0}</p>
                  <p className="text-sm text-gray-400">Пост</p>
                </div>
              </div>

              <hr className="border-gray-200" />

              {teacherId ? (
                <Link
                  to={`/teachers/${teacherId}`}
                  className="flex items-center justify-center gap-2 border border-primary-200 rounded-full py-3 text-sm font-semibold text-primary-500 hover:bg-primary-50 transition"
                >
                  Бусад хичээлүүд харах
                  <ArrowRight className="size-5" />
                </Link>
              ) : (
                <button className="flex items-center justify-center gap-2 border border-primary-200 rounded-full py-3 text-sm font-semibold text-primary-500 hover:bg-primary-50 transition w-full cursor-pointer">
                  Бусад хичээлүүд харах
                  <ChevronRight className="size-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
